import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { UserProfile, UserPreferences, UserRole } from '@/types';
import oktaAuth from '@/config/okta';
import api from '@/services/api';
import { usersApi, type UserJurisdictionDto } from '@/services/usersApi';
import { identifyFullStoryUser, anonymizeFullStoryUser } from '@/config/fullstory';
import { setRollbarPerson, clearRollbarPerson } from '@/config/rollbar';

export const useUserStore = defineStore('user', () => {
  const profile = ref<UserProfile | null>(null);
  const preferences = ref<UserPreferences | null>(null);
  const featureFlags = ref<Record<string, boolean>>({});
  const jurisdictions = ref<UserJurisdictionDto[]>([]);
  const jurisdictionsLoaded = ref(false);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const blockReason = ref<string | null>(null);
  // Track the user's actual database role (not the switched role for dev testing)
  const databaseRole = ref<UserRole>('learner');

  const isAuthenticated = computed(() => profile.value !== null);

  const initials = computed(() => {
    if (!profile.value) return '';
    return `${profile.value.firstName[0]}${profile.value.lastName[0]}`;
  });

  const fullName = computed(() => {
    if (!profile.value) return '';
    return `${profile.value.firstName} ${profile.value.lastName}`;
  });

  const userRole = computed(() => profile.value?.role ?? 'learner');

  const isOrgAdmin = computed(() =>
    profile.value?.role === 'org_admin' || profile.value?.role === 'super_admin'
  );

  const isSuperAdmin = computed(() => profile.value?.role === 'super_admin');

  // Onboarding is required for learners and org admins. SuperAdmin (internal
  // staff) is the only role that skips it. Anyone else with a recorded
  // OnboardingCompletedAt is also done.
  const isOnboardingComplete = computed(() =>
    !!profile.value?.onboardingCompletedAt || databaseRole.value === 'super_admin'
  );

  // Get the user's organization ID (for org admin operations)
  const organizationId = computed(() => {
    const orgId = profile.value?.organizationId;
    return orgId ? parseInt(orgId, 10) : null;
  });

  // Whether the user has super_admin capability based on database role (not current switched role)
  const canBeSuperAdmin = computed(() => databaseRole.value === 'super_admin');

  // Whether the user can switch roles (only non-learner database roles can use the dev role switcher)
  const canSwitchRoles = computed(() => databaseRole.value !== 'learner');

  // Lowercased set of credential type codes covered by the user's jurisdictions.
  // Powers Phase 13 jurisdiction-scoped learner nav: empty set (unloaded or user
  // has no jurisdictions) means "don't know yet — show everything" so the nav
  // doesn't collapse during onboarding or before the fetch resolves.
  const userCredentialTypeCodes = computed<Set<string>>(() => {
    const codes = new Set<string>();
    for (const j of jurisdictions.value) {
      if (j.credentialType) codes.add(j.credentialType.toLowerCase());
    }
    return codes;
  });

  function hasRole(requiredRole: 'learner' | 'org_admin' | 'super_admin'): boolean {
    if (!profile.value) return false;
    const roleHierarchy = { learner: 0, org_admin: 1, super_admin: 2 };
    return roleHierarchy[databaseRole.value] >= roleHierarchy[requiredRole];
  }

  function mapPlatformRoleToUserRole(platformRole: string | undefined): UserRole {
    switch (platformRole?.toLowerCase()) {
      case 'superadmin':
        return 'super_admin';
      case 'orgowner':
      case 'orgmanager':
        return 'org_admin';
      default:
        return 'learner';
    }
  }

  async function fetchProfile() {
    isLoading.value = true;
    error.value = null;
    blockReason.value = null;
    try {
      const user = await oktaAuth.getUser();
      if (user) {
        // Build profile in a local variable first — don't set the reactive ref until
        // the sync call confirms the user is not deactivated.  Setting profile.value
        // before sync completes would briefly expose a non-null profile (from Okta
        // claims alone) that lacks deactivation info, which can cause the onboarding
        // page to flash before the deactivated interstitial takes over.
        let userProfile: UserProfile = {
          id: user.sub || '',
          firstName: (user.given_name as string) || '',
          lastName: (user.family_name as string) || '',
          email: (user.email as string) || '',
          organization: 'BARBRI',
          jurisdictions: [],
          role: 'learner',
        };

        // Halo user ID from the backend, when sync succeeds. Error reporting
        // counts users by this ID only, never the Okta sub fallback above.
        let internalUserId: string | undefined;

        // Try to sync with backend to get database role
        try {
          const syncResponse = await api.post('/me/sync');
          const backendUser = syncResponse.data;
          internalUserId = backendUser.id?.toString();

          databaseRole.value = mapPlatformRoleToUserRole(backendUser.platformRole);

          // Update profile with backend data, but default role to 'learner' so the
          // role switcher reflects the current view (not the database role) on first load.
          userProfile = {
            id: backendUser.id?.toString() || user.sub || '',
            firstName: backendUser.firstName || (user.given_name as string) || '',
            lastName: backendUser.lastName || (user.family_name as string) || '',
            email: backendUser.email || (user.email as string) || '',
            organization: backendUser.organizationName || 'BARBRI',
            organizationId: backendUser.organizationId?.toString(),
            jurisdictions: [],
            role: 'learner',
            onboardingCompletedAt: backendUser.onboardingCompletedAt ?? null,
          };
        } catch (syncError: any) {
          // Deactivation 403 — leave profile as null so the router guard redirects
          // to the AccountDeactivated interstitial.
          const code = syncError?.response?.data?.code;
          if (code === 'USER_DEACTIVATED' || code === 'ORG_DEACTIVATED' || code === 'NO_ORGANIZATION') {
            profile.value = null;
            blockReason.value = code;
            return;
          }
          // Backend sync failed - profile still shows from Okta, but no admin capabilities
          console.warn('Failed to sync with backend:', syncError);
          databaseRole.value = 'learner';
        }

        // Assign profile only after sync resolved (active) or failed non-fatally.
        profile.value = userProfile;

        // Fetch feature flags (non-blocking)
        api.get<Record<string, boolean>>('/features')
          .then(res => { featureFlags.value = res.data; })
          .catch(() => { /* Feature flags unavailable — defaults to empty */ });

        identifyFullStoryUser({
          id: userProfile.id,
          email: userProfile.email,
          firstName: userProfile.firstName,
          lastName: userProfile.lastName,
          role: userProfile.role,
          organization: userProfile.organization,
          organizationId: userProfile.organizationId,
        });
        if (internalUserId) setRollbarPerson(internalUserId);
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to load profile';
    } finally {
      isLoading.value = false;
    }
  }

  async function updateProfile(updates: Partial<UserProfile>) {
    if (!profile.value) return;
    isLoading.value = true;
    try {
      // Call backend API to update profile
      const response = await api.put('/me', {
        firstName: updates.firstName,
        lastName: updates.lastName,
      });
      const backendUser = response.data;

      // Update local profile with backend response
      profile.value = {
        ...profile.value,
        firstName: backendUser.firstName || profile.value.firstName,
        lastName: backendUser.lastName || profile.value.lastName,
        email: backendUser.email || profile.value.email,
        organization: backendUser.organizationName || profile.value.organization,
      };
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to update profile';
      throw e;
    } finally {
      isLoading.value = false;
    }
  }

  async function fetchJurisdictions() {
    if (!profile.value) return;
    try {
      jurisdictions.value = await usersApi.getMyJurisdictions();
    } catch {
      // Non-fatal — leave jurisdictions empty; nav falls back to showing all credentials.
    } finally {
      // `jurisdictionsLoaded` means "the attempt is done" (the nav gates on it), so set
      // it regardless of outcome. On error the list stays empty → show-all fallback.
      jurisdictionsLoaded.value = true;
    }
  }

  async function ensureJurisdictionsLoaded() {
    if (!jurisdictionsLoaded.value && profile.value) {
      await fetchJurisdictions();
    }
  }

  function logout() {
    profile.value = null;
    preferences.value = null;
    jurisdictions.value = [];
    jurisdictionsLoaded.value = false;
    blockReason.value = null;
    anonymizeFullStoryUser();
    clearRollbarPerson();
    // Note: actual Okta signOut is handled by LogoutView
  }

  function setRole(role: 'learner' | 'org_admin' | 'super_admin') {
    if (profile.value) {
      profile.value = { ...profile.value, role };
    }
  }

  return {
    profile,
    preferences,
    isLoading,
    error,
    featureFlags,
    jurisdictions,
    jurisdictionsLoaded,
    isAuthenticated,
    initials,
    fullName,
    userRole,
    isOrgAdmin,
    isSuperAdmin,
    isOnboardingComplete,
    organizationId,
    canBeSuperAdmin,
    blockReason,
    canSwitchRoles,
    userCredentialTypeCodes,
    hasRole,
    fetchProfile,
    fetchJurisdictions,
    ensureJurisdictionsLoaded,
    updateProfile,
    logout,
    setRole,
  };
});
